/**
 * @file src/demo/main.js
 *
 * Demo orchestrator. Mounts all the components and wires them together for
 * the new signup-forms data model.
 *
 * In Liquid, the wrapper does essentially the same thing — see the porting
 * checklist in `liquid/wine-club-signup.liquid`.
 *
 * If `window.WineClubMockMode === true`, the API calls are stubbed and
 * `window.WineClubSeed.init` provides a fake `/initialize` payload.
 */

import { initializeSignup, previewSignup, confirmSignup, getCustomerPaymentMethods, isCustomerExistsError, isTokenError } from 'wcs/api';
import { tokenizeCard } from 'wcs/cardVault';
import { createStore } from 'wcs/state';
import { log, warn, isDebugEnabled } from 'wcs/debug';
import { getCustomer, fetchAuthToken, clearAuthToken } from 'wcs/auth';

import { mountErrorSurface }   from 'wcs/ErrorSurface';
import { mountLoginGate }      from 'wcs/LoginGate';
import { mountClubStep }       from 'wcs/ClubStep';
import { mountBoxBuilder }     from 'wcs/BoxBuilder';
import { mountMemberInfoStep } from 'wcs/MemberInfoStep';
import { mountDeliveryStep }   from 'wcs/DeliveryStep';
import { mountPaymentStep }    from 'wcs/PaymentStep';
import { mountSubmitBar }      from 'wcs/SubmitBar';

const seed = window.WineClubSeed || {};

log('boot', 'orchestrator starting', {
  mockMode: !!window.WineClubMockMode,
  mockScenario: window.WineClubMockScenario || null,
  debug: isDebugEnabled(),
});

/* ============================================================================
 * Clubs (brand-keyed, settings-driven)
 *
 * The section captures the current brand via the theme's `brand-detect`
 * snippet and emits two globals:
 *   window.WineClubBrand        e.g. "hall" | "walt" | "baca" | "michelfoch"
 *   window.WineClubClubsConfig  JSON string: { <brand>: Club[] , ... }
 * where Club = { id, name, sellingPlanGid, sizes: [ { variantGid, name } ] }.
 *
 * The customer picks a club, then a size (bottle count) within it. The selling
 * plan comes from the club; the variant comes from the chosen size. We send a
 * SINGLE variant + selling plan (quantity 1 — the size variant IS the bottle
 * count); the API expands it into the full subscription server-side.
 * ============================================================================ */

function getClubs() {
  if (typeof window === 'undefined') return [];
  const brand = window.WineClubBrand || '';
  let config = {};
  try {
    config = JSON.parse(window.WineClubClubsConfig || '{}');
  } catch (err) {
    warn('boot', 'WineClubClubsConfig is not valid JSON', err);
    return [];
  }
  const list = config[brand] || config.default || [];
  return Array.isArray(list) ? list : [];
}

// The line item for a chosen size. Quantity is always 1 — the size variant
// itself encodes the bottle count.
function linesForSize(size) {
  return size && size.variantGid
    ? [{ variantGId: size.variantGid, quantity: 1 }]
    : [];
}

// Pickup locations for the detected brand. seed.pickupLocations is brand-keyed
// ({ hall:[...], walt:[...], default:[...] }); a brand with no locations returns
// [], in which case DeliveryStep hides the Ship/Pickup radio and locks to
// shipping.
function getPickupLocations() {
  const brand = (typeof window !== 'undefined' && window.WineClubBrand) || '';
  const byBrand = seed.pickupLocations || {};
  const list = byBrand[brand] || byBrand.default || [];
  return Array.isArray(list) ? list : [];
}

// CX-4731 — club-driven delivery. The selected club's `delivery` field
// ("shipping" | "pickup") fixes the delivery method for that club: the
// Adventure Allocation Club ships (address step shown); the Pick-Up Club is
// PICKUP (address hidden, pickup location pre-set). Returns the deliveryMethod
// patch to apply, or null when the club doesn't declare a delivery mode (then
// the default Ship/Pickup behavior stands).
function deliveryForClub(club) {
  if (!club || !club.delivery) return null;
  const mode = String(club.delivery).toLowerCase();
  if (mode === 'pickup') {
    return {
      type: 'PICKUP',
      pickupLocationId: club.pickupLocationId || getPickupLocations()[0]?.value || null,
    };
  }
  return { type: 'SHIPPING', pickupLocationId: null };
}

// Toggle the subtle loading indicator shown while bootstrap() runs (auth token
// exchange + /initialize) before the form renders.
function setLoading(on) {
  const el = document.getElementById('wcs-loading');
  if (el) el.hidden = !on;
}

/* ============================================================================
 * State
 * ============================================================================ */

const store = createStore({
  init: null,                      // /initialize response, cached
  activeOptionIndex: 0,            // which option in the active form is active

  selection: {
    signupToken: null,
    signupFormId: null,
    clubId: null,
    sellingPlanGId: null,
    lines: [],
    customer: { email: '', firstName: '', lastName: '', phone: '', birthdate: '' },
    deliveryMethod: { type: 'SHIPPING', address: {}, pickupLocationId: null },
    paymentMethodGId: null,
    newCard: null,
    paymentName: { firstName: '', lastName: '' },
    useShippingAsBilling: true,
    billingAddress: {},
    customAttributes: [],          // [{ key, value }] — wire merchant-custom fields here later
  },
});

if (typeof window !== 'undefined' && isDebugEnabled()) {
  window.wcStore = store;
  store.subscribe((next) => log('state', 'updated', next));
}

/* ============================================================================
 * Mount
 * ============================================================================ */

const errorSurface = mountErrorSurface(document.getElementById('wcs-error-surface'));

const loginGate = mountLoginGate(document.getElementById('wcs-login-gate'), {
  // loginUrl intentionally omitted — LoginGate falls through to buildLoginUrl(),
  // which reads window.WineClubLoginUrl (set by the Liquid section, with a guard
  // for the `#log-in-here` placeholder). Hardcoding a value here overrides that.
  //
  // Guest mode was removed: the form now requires a Shopify-signed-in customer
  // because the signup API expects an Authorization JWT obtained via the
  // shop's app-proxy customer endpoint (see auth.js → fetchAuthToken).
  onProceed: () => {
    document.getElementById('wcs-form-body').hidden = false;
    bootstrap();
  },
});

// The loader (#wcs-loading) is visible on initial load. If the customer is
// logged OUT, LoginGate just rendered the login option — hide the loader now.
// If logged IN, LoginGate auto-proceeded and bootstrap() hides the loader once
// the customer-information fields render.
if (!getCustomer()) setLoading(false);

let clubStep, memberInfoStep, deliveryStep, paymentStep, submitBar, boxBuilder = null;

async function bootstrap() {
  setLoading(true);
  /* ------------------------------------------------------------------------
   * Exchange the Shopify session cookie for an Awtomic JWT BEFORE any
   * signup call. api.js attaches this token as `Authorization` on every
   * /signup/* request. If the exchange fails, the customer either isn't
   * signed in or the app proxy is misconfigured — surface a clear error
   * and re-show the LoginGate.
   * ---------------------------------------------------------------------- */
  log('boot', 'bootstrap: fetching auth token');
  let customerId = null;
  try {
    const auth = await fetchAuthToken();
    customerId = auth.customerId;
    log('boot', 'auth token acquired', { customerId });
  } catch (authErr) {
    log('boot', 'auth token fetch failed', authErr);
    clearAuthToken();
    if (authErr.code === 'AUTH_NOT_SIGNED_IN') {
      document.getElementById('wcs-form-body').hidden = true;
      loginGate.forceLogin('Please sign in to your Shopify account to continue.');
    } else {
      errorSurface.show(authErr.message || 'Could not verify your sign-in. Please refresh and try again.');
    }
    setLoading(false);
    return;
  }

  log('boot', 'bootstrap: calling /initialize and /customers/:id/payment-methods in parallel');
  let init;
  let savedPaymentMethodsFromEndpoint = [];
  try {
    // Build the Shopify Customer GID for /initialize. Sources, in order:
    //   1. window.WineClubCustomer.id (Liquid customer object) wrapped as GID
    //   2. The numeric customerId returned by fetchAuthToken (JWT claim)
    // Sign-in is forced so one of these is present.
    const shopifyCustomerId =
      (typeof window !== 'undefined' && window.WineClubCustomer?.id) || customerId;
    const customerGId = shopifyCustomerId
      ? `gid://shopify/Customer/${shopifyCustomerId}`
      : undefined;

    // /initialize is the gating call — if it fails we can't render the form.
    // The payment-methods call is best-effort: a 404 / 500 there shouldn't
    // block signup, it just means the saved-card dropdown won't have data
    // from that source. We still fall through to init.customer.paymentMethods.
    const [initRes, paymentMethodsRes] = await Promise.allSettled([
      initializeSignup({ customerGId }),
      getCustomerPaymentMethods(customerId),
    ]);

    if (initRes.status === 'rejected') throw initRes.reason;
    init = initRes.value;

    if (paymentMethodsRes.status === 'fulfilled') {
      savedPaymentMethodsFromEndpoint = paymentMethodsRes.value || [];
      log('boot', 'payment methods endpoint returned', { count: savedPaymentMethodsFromEndpoint.length });
    } else {
      log('boot', 'payment methods endpoint failed (non-blocking)', paymentMethodsRes.reason);
    }
  } catch (err) {
    log('boot', 'initialize failed', err);
    errorSurface.show(err.message || 'Could not start the signup. Please refresh and try again.');
    setLoading(false);
    return;
  }
  log('boot', 'initialize ok', {
    signupForms: init.signupForms?.length || 0,
    hasCustomer: !!init.customer,
  });

  // Prefill priority: Awtomic's /initialize response (when we have a customer
  // token) wins; the Shopify customer object (set by Liquid from `{{ customer }}`)
  // is a fallback so we still prefill when running without a token (Option C).
  const shopifyCustomer = getCustomer();
  log('boot', 'prefill sources', {
    awtomic: !!init.customer,
    shopify: !!shopifyCustomer,
  });

  // Clubs come from the section settings (brand-keyed). Default to the first
  // club for the detected brand; the customer can switch in ClubStep.
  const signupFormId =
    (typeof window !== 'undefined' && window.WineClubSignupFormId) || '1';
  const clubs = getClubs();
  const defaultClub = clubs[0] || null;
  const defaultSize = defaultClub?.sizes?.[0] || null;
  log('boot', 'clubs', {
    brand: (typeof window !== 'undefined' && window.WineClubBrand) || null,
    count: clubs.length,
    defaultClub: defaultClub?.id || null,
    defaultSize: defaultSize?.variantGid || null,
  });

  // Merge saved payment methods from two sources (deduped by gId):
  //   - init.customer.paymentMethods (from /signup/initialize)
  //   - savedPaymentMethodsFromEndpoint (from GET /customers/:id/payment-methods)
  // The endpoint is the authoritative source for the saved-card UI; init's
  // copy is kept as a fallback for shops where the dedicated endpoint
  // returns nothing for any reason.
  const mergedSavedMethods = mergeSavedPaymentMethods(
    init.customer?.paymentMethods || [],
    savedPaymentMethodsFromEndpoint
  );
  // Drop expired cards before the dropdown ever sees them — no point in
  // offering a card the customer can't use.
  const usableSavedMethods = mergedSavedMethods.filter((pm) => !isCardExpired(pm));
  // Default-select the first usable saved card (prefers `isDefault`).
  // When the list is empty, leave paymentMethodGId null and the form will
  // show the new-card UI with no dropdown.
  const defaultSavedMethod = pickDefaultSavedMethod(usableSavedMethods);
  log('boot', 'saved payment methods', {
    fromInit: init.customer?.paymentMethods?.length || 0,
    fromEndpoint: savedPaymentMethodsFromEndpoint.length,
    merged: mergedSavedMethods.length,
    usable: usableSavedMethods.length,
    defaultSelected: defaultSavedMethod?.gId || null,
  });

  store.update({
    init,
    selection: {
      ...store.get().selection,
      signupToken:    init.signupToken,
      // signup_form_id is still required by the API; '1' works because the
      // server runs with form validation disabled. The offer comes from the
      // selected club (single variant + plan); the API expands it server-side.
      signupFormId:   signupFormId,
      clubId:         defaultClub?.id || null,
      sellingPlanGId: defaultClub?.sellingPlanGid || null,
      lines:          linesForSize(defaultSize),
      customer: {
        ...store.get().selection.customer,
        email:     init.customer?.email     || shopifyCustomer?.email     || '',
        firstName: init.customer?.firstName || shopifyCustomer?.firstName || '',
        lastName:  init.customer?.lastName  || shopifyCustomer?.lastName  || '',
        phone:     init.customer?.phone     || shopifyCustomer?.phone     || '',
      },
      deliveryMethod: {
        ...store.get().selection.deliveryMethod,
        // US-only shipping for now: default the country to US (prefill an
        // existing address, but force countryCode so the dropdown shows US).
        address: { ...(init.customer?.defaultAddress || shopifyCustomer?.defaultAddress || {}), countryCode: 'US' },
        // CX-4731 — seed the delivery method from the default club so the
        // form mounts in the correct mode (SHIPPING for Adventure Allocation,
        // PICKUP for Pick-Up Club) before the customer interacts.
        ...(deliveryForClub(defaultClub) || {}),
      },
      paymentName: {
        firstName: store.get().selection.paymentName.firstName ||
                   init.customer?.firstName || shopifyCustomer?.firstName || '',
        lastName:  store.get().selection.paymentName.lastName ||
                   init.customer?.lastName  || shopifyCustomer?.lastName  || '',
      },
      // Pre-select the saved card so PaymentStep mounts with the saved-card
      // UI active (new-card form hidden). null when no usable saved card.
      paymentMethodGId: defaultSavedMethod?.gId || null,
    },
  });

  mountSections(init, usableSavedMethods);
  setLoading(false);
}

function mergeSavedPaymentMethods(fromInit, fromEndpoint) {
  const seen = new Set();
  const out = [];
  // Endpoint first — treat it as authoritative when both have the same gId.
  for (const pm of fromEndpoint) {
    if (!pm || !pm.gId || seen.has(pm.gId)) continue;
    seen.add(pm.gId);
    out.push(pm);
  }
  for (const pm of fromInit) {
    if (!pm || !pm.gId || seen.has(pm.gId)) continue;
    seen.add(pm.gId);
    out.push(pm);
  }
  return out;
}

/**
 * A saved payment method is expired if its expiry month/year is in the past.
 * `expiryMonth` is 1-indexed (1 = January). Cards with missing expiry data
 * are NOT considered expired (we can't tell, so don't filter them).
 */
function isCardExpired(pm) {
  if (!pm || !pm.expiryYear) return false;
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  if (pm.expiryYear < currentYear) return true;
  if (pm.expiryYear === currentYear && (pm.expiryMonth || 0) < currentMonth) return true;
  return false;
}

/**
 * Pick the saved method to default-select on page load. Prefers the one
 * flagged `isDefault`; otherwise the first non-expired entry. Returns null
 * if no usable method exists (form will show the new-card UI).
 */
function pickDefaultSavedMethod(methods) {
  if (!methods || !methods.length) return null;
  const usable = methods.filter((pm) => !isCardExpired(pm));
  if (!usable.length) return null;
  return usable.find((pm) => pm.isDefault) || usable[0];
}

function mountSections(init, savedPaymentMethods = []) {
  const s    = store.get().selection;
  const form = activeForm();

  /* ───────── ClubStep (brand-keyed, settings-driven: club → size) ───────── */
  clubStep = mountClubStep(document.getElementById('wcs-club-step'), {
    clubs: getClubs(),
    value: { clubId: s.clubId, sizeVariantGid: s.lines?.[0]?.variantGId || null },
    onSelectClub: (club) => {
      // Selling plan comes from the club; auto-select its first size (the
      // size supplies the variant). ClubStep surfaces the size picker.
      const firstSize = club.sizes?.[0] || null;
      store.updatePath('selection.clubId', club.id);
      store.updatePath('selection.sellingPlanGId', club.sellingPlanGid || null);
      store.updatePath('selection.lines', linesForSize(firstSize));

      // CX-4731 — the club fixes the delivery method. Patch state and the
      // DeliveryStep UI so the Pick-Up Club hides the shipping-address step
      // (PICKUP + pre-set location) and the Adventure Allocation Club shows it.
      const dm = deliveryForClub(club);
      if (dm) {
        store.update((state) => ({
          ...state,
          selection: {
            ...state.selection,
            deliveryMethod: { ...state.selection.deliveryMethod, ...dm },
          },
        }));
        if (deliveryStep) {
          const cur = store.get().selection.deliveryMethod;
          deliveryStep.setValue(cur);
          // Delivery is club-driven — hide the manual Ship/Pickup radio.
          deliveryStep.setAllowDeliveryChange(false);
        }
      }
      schedulePreview();
    },
    onSelectSize: (size) => {
      store.updatePath('selection.lines', linesForSize(size));
      schedulePreview();
    },
  });

  /* ───────── BoxBuilder (conditional) ───────── */
  maybeShowBoxBuilder(activeOption());

  /* ───────── MemberInfoStep ───────── */
  // DOB visibility: Awtomic's form.birthDateRequired wins when true; the theme
  // setting `show_birthdate` can force-show it even when Awtomic says no.
  // (Useful for shops where compliance is enforced by the merchant outside
  // Awtomic, or when Awtomic returns no matching form at all.)
  const showBirthdate =
    !!form?.birthDateRequired ||
    (typeof window !== 'undefined' && !!window.WineClubShowBirthdate);

  memberInfoStep = mountMemberInfoStep(document.getElementById('wcs-member-info-step'), {
    value: s.customer,
    birthDateRequired: showBirthdate,
    // Sign-in is forced, so the email is the logged-in customer's — lock it.
    lockEmail: !!s.customer.email,
    onChange: (patch) => {
      store.update((state) => ({
        ...state,
        selection: { ...state.selection, customer: { ...state.selection.customer, ...patch } },
      }));
      schedulePreview();
    },
  });

  /* ───────── DeliveryStep ───────── */
  // CX-4731 — delivery is CLUB-DRIVEN here: the selected club's `delivery`
  // field fixes SHIPPING vs PICKUP, so the manual Ship/Pickup radio is hidden
  // whenever any club declares a delivery mode. (Falls back to the location-
  // based behavior if no club declares one.)
  const pickupLocations = getPickupLocations();
  const clubDrivenDelivery = getClubs().some((c) => c && c.delivery);
  deliveryStep = mountDeliveryStep(document.getElementById('wcs-delivery-step'), {
    value: s.deliveryMethod,
    countries: seed.countries || [],
    provinces: seed.provincesByCountry?.[s.deliveryMethod.address?.countryCode || 'US'] || [],
    pickupLocations: pickupLocations,
    allowDeliveryChange: clubDrivenDelivery ? false : pickupLocations.length > 0,
    onChange: (patch) => {
      store.update((state) => {
        const dm = state.selection.deliveryMethod;
        const next = { ...dm };
        if (patch.type !== undefined) next.type = patch.type;
        if (patch.address) next.address = { ...dm.address, ...patch.address };
        if (patch.pickupLocationId !== undefined) next.pickupLocationId = patch.pickupLocationId;
        return { ...state, selection: { ...state.selection, deliveryMethod: next } };
      });
      schedulePreview();
    },
    onCountryChange: (cc) => {
      const provinces = seed.provincesByCountry?.[cc] || [];
      deliveryStep.setProvinces(provinces);
    },
  });

  /* ───────── PaymentStep (conditional on form.requiresPaymentMethod) ───────── */
  const paymentSlot = document.getElementById('wcs-payment-step');
  if (form?.requiresPaymentMethod !== false) {
    paymentSlot.hidden = false;
    paymentStep = mountPaymentStep(paymentSlot, {
      value: {
        firstName: s.paymentName.firstName,
        lastName:  s.paymentName.lastName,
        paymentMethodGId: s.paymentMethodGId,
        useShippingAsBilling: s.useShippingAsBilling,
        billingAddress: s.billingAddress,
      },
      savedPaymentMethods: savedPaymentMethods,
      countries: seed.countries || [],
      provinces: seed.provincesByCountry?.['US'] || [],
      onChange: (patch) => {
        store.update((state) => {
          const sel = state.selection;
          const next = { ...sel };
          if (patch.paymentMethodGId !== undefined) next.paymentMethodGId = patch.paymentMethodGId;
          if (patch.useShippingAsBilling !== undefined) next.useShippingAsBilling = patch.useShippingAsBilling;
          if (patch.firstName !== undefined) next.paymentName = { ...sel.paymentName, firstName: patch.firstName };
          if (patch.lastName  !== undefined) next.paymentName = { ...next.paymentName, lastName: patch.lastName };
          if (patch.billingAddress) next.billingAddress = { ...sel.billingAddress, ...patch.billingAddress };
          return { ...state, selection: next };
        });
        if (patch.paymentMethodGId !== undefined || patch.useShippingAsBilling !== undefined) {
          schedulePreview();
        }
      },
      onCountryChange: (cc) => {
        const provinces = seed.provincesByCountry?.[cc] || [];
        paymentStep.setProvinces(provinces);
      },
    });
  } else {
    paymentSlot.hidden = true;
    paymentStep = null;
  }

  /* ───────── SubmitBar ───────── */
  submitBar = mountSubmitBar(document.getElementById('wcs-submit-bar'), {
    submitLabel: 'Join the club',
    // CX-4671: two required consent checkboxes — (1) continuous-membership
    // billing disclosure, (2) Terms of Use + Privacy Policy. Copy comes from
    // the section settings (editable in the theme editor); falls back to the
    // generic label if a shop hasn't set them.
    termsLabelHtml: (typeof window.WineClubConsentBilling === 'string' && window.WineClubConsentBilling.trim())
      ? window.WineClubConsentBilling
      : undefined,
    termsLabel: 'I agree to the wine-club terms and conditions, including monthly billing.',
    terms2LabelHtml: (typeof window.WineClubConsentPolicies === 'string' && window.WineClubConsentPolicies.trim())
      ? window.WineClubConsentPolicies
      : undefined,
    requiresDisclosure: form?.requiresDisclosure !== false,
    onSubmit: handleSubmit,
  });

  /* ───────── CX-4671 — Terms/Privacy policy modal ─────────
   * Intercept clicks on the consent "Terms of Use" / "Privacy Policy" links
   * and open an on-page modal whose content is the shop's policy body
   * (Settings → Policies) — a single source of truth. If a policy isn't set
   * (window.WineClubHas*Policy false), the link navigates to its /pages/
   * fallback instead. The modal markup is rendered server-side by the section. */
  (function setupPolicyModal() {
    const modal = document.getElementById('wcs-policy-modal');
    const root = document.getElementById('wcs-root');
    if (!modal || !root) return; // no policies set -> links keep their /pages/ fallback
    const panels = modal.querySelectorAll('[data-wcs-policy-panel]');
    const showPanel = (which) => {
      let found = false;
      panels.forEach((p) => {
        const match = p.getAttribute('data-wcs-policy-panel') === which;
        p.hidden = !match;
        if (match) found = true;
      });
      return found;
    };
    const openModal = (which) => {
      if (!showPanel(which)) return false;
      if (typeof modal.showModal === 'function') modal.showModal();
      else modal.setAttribute('open', '');
      return true;
    };
    const closeModal = () => {
      if (typeof modal.close === 'function') modal.close();
      else modal.removeAttribute('open');
    };
    // Close via the × button or a backdrop click (Esc is native to <dialog>).
    modal.addEventListener('click', (e) => {
      if (e.target.closest('[data-wcs-policy-close]') || e.target === modal) closeModal();
    });
    // Intercept the consent policy links inside the form.
    root.addEventListener('click', (e) => {
      const a = e.target.closest('a[href]');
      if (!a || !root.contains(a)) return;
      const href = (a.getAttribute('href') || '').toLowerCase();
      let which = null;
      if (href.includes('terms-of-service') || href.includes('terms-of-use')) which = 'terms';
      else if (href.includes('privacy-policy') || href.includes('privacy')) which = 'privacy';
      if (!which) return;
      const has = which === 'terms' ? window.WineClubHasTermsPolicy : window.WineClubHasPrivacyPolicy;
      if (!has) return; // policy not in Settings -> let the link navigate to /pages/ fallback
      if (openModal(which)) e.preventDefault();
    });
  })();
}

/**
 * Re-applies per-form UI flags when the active form changes (multi-form
 * stores). Components stay mounted; we just toggle their visible state.
 */
function applyFormFlags(form) {
  if (!form) return;
  const showBirthdate =
    !!form.birthDateRequired ||
    (typeof window !== 'undefined' && !!window.WineClubShowBirthdate);
  if (memberInfoStep) memberInfoStep.setBirthDateRequired(showBirthdate);
  if (deliveryStep)   deliveryStep.setAllowDeliveryChange(form.allowDeliveryChange !== false);
  if (submitBar)      submitBar.setRequiresDisclosure(form.requiresDisclosure !== false);

  const paymentSlot = document.getElementById('wcs-payment-step');
  paymentSlot.hidden = form.requiresPaymentMethod === false;
  // (We don't tear down PaymentStep on flag change — toggling visibility is
  // sufficient; if you want a real re-mount, destroy() then mount.)
}

function maybeShowBoxBuilder(option) {
  const slot = document.getElementById('wcs-box-builder');
  const hasBox = !!option?.defaultSelection?.selections?.length;

  if (boxBuilder) { boxBuilder.destroy(); boxBuilder = null; }

  if (!hasBox) {
    slot.hidden = true;
    return;
  }

  slot.hidden = false;
  boxBuilder = mountBoxBuilder(slot, {
    title: option.defaultSelection.name || 'Build your box',
    value: store.get().selection.lines,
    onChange: (lines) => {
      store.updatePath('selection.lines', lines);
      schedulePreview();
    },
  });
}

/* ============================================================================
 * Preview debounce
 *
 * (All API calls go directly to initializeSignup/previewSignup/confirmSignup
 * from api.js. When WineClubMockMode is true those functions are transparently
 * served by mockApi.js — same code path, mocked at the fetch boundary, so the
 * orchestrator doesn't have to know whether it's mocking or not.)
 * ============================================================================ */

let previewTimer = null;
function schedulePreview() {
  if (previewTimer) clearTimeout(previewTimer);
  previewTimer = setTimeout(runPreview, 400);
}

async function runPreview() {
  const sel = store.get().selection;
  if (!sel.signupToken || !sel.signupFormId || !sel.sellingPlanGId || !sel.lines.length) return;
  if (!sel.customer.email) return;
  try {
    await previewSignup(buildRequestBody(sel));
  } catch (err) {
    console.warn('[wcs/demo] preview error', err);
  }
}

/* ============================================================================
 * Submit
 * ============================================================================ */

async function handleSubmit() {
  log('ui', 'submit pressed');
  errorSurface.hide();
  submitBar.setBusy(true);
  submitBar.setError(null);

  try {
    const sel = store.get().selection;
    const form = activeForm();

    /* Phone is required — enforce a complete 10-digit number before continuing. */
    const phoneDigits = (sel.customer.phone || '').replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      memberInfoStep.setFieldError('phone', 'Please enter a valid phone number.');
      errorSurface.show('A phone number is required to continue.');
      return;
    }
    memberInfoStep.setFieldError('phone', null);

    /* Shipping phone is required too — same 10-digit rule as the member phone,
     * but only when shipping (PICKUP has no address phone). */
    if (sel.deliveryMethod?.type === 'SHIPPING') {
      const shipPhoneDigits = (sel.deliveryMethod.address?.phone || '').replace(/\D/g, '');
      if (shipPhoneDigits.length < 10) {
        deliveryStep.setFieldError('address.phone', 'Please enter a valid phone number.');
        errorSurface.show('A shipping phone number is required to continue.');
        return;
      }
      deliveryStep.setFieldError('address.phone', null);
    }

    /* Step 1: tokenize the card if one is required and the user is using a new card.
     * In mock mode, cardVault.tokenizeCard transparently routes through mockApi
     * (no special-case here). */
    if (form?.requiresPaymentMethod !== false && !sel.paymentMethodGId && !sel.newCard?.sessionId) {
      const cardData = paymentStep?.getCardData();
      if (!cardData || !cardData.number) {
        errorSurface.show('Please enter your card details to continue.');
        return;
      }
      try {
        const sessionId = await tokenizeCard(cardData);
        store.update((state) => ({
          ...state,
          selection: { ...state.selection, newCard: { sessionId } },
        }));
        // Intentionally do NOT clear the card fields here: a later step
        // (preview/confirm) may bail, and wiping the inputs would force the
        // customer to re-type. The raw PAN lives only in PaymentStep's local
        // copy (never persisted to state); the success path hides the form.
      } catch (vaultErr) {
        if (vaultErr.field) paymentStep.setFieldError(vaultErr.field, vaultErr.message);
        else errorSurface.show(vaultErr.message || 'We could not validate your card. Please try again.');
        return;
      }
    }

    /* Step 2: re-preview. /signup/preview creates the Shopify subscription
     * draft and returns its draftGId + the available deliveryOptions; it also
     * surfaces compliance / address errors. */
    const sel2 = store.get().selection;
    const body = buildRequestBody(sel2);
    const preview = await previewSignup(body);
    if (preview.errors && preview.errors.length) {
      errorSurface.show('We could not confirm your signup.', { details: preview.errors.map((e) => e.message || String(e)) });
      return;
    }
    if (!preview.draftGId) {
      errorSurface.show('We could not start your subscription. Please try again.');
      return;
    }

    /* Pick the delivery option matching the chosen method; fall back to the
     * first option the server offered. preview only returns deliveryOptions
     * for SHIPPING with a resolved address — PICKUP (and rate-less shipping)
     * legitimately yield none, so an empty list is NOT a hard block: let
     * /signup/confirm be the authority and only send a code when we have one. */
    const deliveryOptions = preview.deliveryOptions || [];
    const deliveryOption =
      deliveryOptions.find((o) => o.type === sel2.deliveryMethod?.type) ||
      deliveryOptions[0];

    /* Step 3: confirm — commit the draft preview created. /signup/confirm
     * takes { signupToken, draftGId, deliveryOptionCode, agreedToTerms },
     * NOT the preview body. */
    const confirmReq = {
      signupToken: sel2.signupToken,
      draftGId: preview.draftGId,
    };
    if (deliveryOption) confirmReq.deliveryOptionCode = deliveryOption.code;
    const result = await confirmSignup(confirmReq);
    errorSurface.hide();
    document.getElementById('wcs-form-body').hidden = true;
    showConfirmation(result, sel2);
  } catch (err) {
    if (isTokenError(err)) {
      errorSurface.show('Your session expired. Please refresh the page.');
    } else if (isCustomerExistsError(err)) {
      loginGate.forceLogin('Looks like you already have an account — please sign in to continue.');
      document.getElementById('wcs-form-body').hidden = true;
    } else {
      errorSurface.show(err.message || 'Something went wrong. Please try again.');
    }
  } finally {
    submitBar.setBusy(false);
  }
}

/* ============================================================================
 * Helpers
 * ============================================================================ */

/**
 * Render the post-signup confirmation screen. Replaces the bare success markup
 * with a styled, personalized panel (see wcs-confirmation.css). The raw
 * subscription GID is intentionally NOT surfaced — it's not customer-facing.
 *
 * @param {Object} result  ConfirmResponse ({ nextBillingDate, orderCreated, … })
 * @param {Object} sel      The selection snapshot used for the submit.
 */
function showConfirmation(result, sel) {
  const firstName = (sel?.customer?.firstName || '').trim();
  const email     = (sel?.customer?.email || '').trim();
  const clubName  = getClubs().find((c) => c.id === sel?.clubId)?.name || '';

  let dateStr = '';
  if (result?.nextBillingDate) {
    const d = new Date(result.nextBillingDate);
    if (!Number.isNaN(d.getTime())) {
      dateStr = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }
  }

  const title = firstName ? `You're in, ${escapeHtml(firstName)}!` : `You're in!`;
  const subtitle = clubName
    ? `Welcome to ${escapeHtml(clubName)}. Your membership is confirmed.`
    : `Your membership is confirmed.`;

  const rows = [];
  if (clubName) {
    rows.push(`<div class="wcs-confirmation__row"><dt>Membership</dt><dd>${escapeHtml(clubName)}</dd></div>`);
  }
  if (dateStr) {
    const label = result?.orderCreated ? 'Next billing date' : 'First billing date';
    rows.push(`<div class="wcs-confirmation__row"><dt>${label}</dt><dd>${escapeHtml(dateStr)}</dd></div>`);
  }
  if (email) {
    rows.push(`<div class="wcs-confirmation__row"><dt>Confirmation sent to</dt><dd>${escapeHtml(email)}</dd></div>`);
  }

  const section = document.createElement('section');
  section.className = 'wcs-section wcs-confirmation';
  section.setAttribute('role', 'status');
  section.setAttribute('aria-live', 'polite');
  section.innerHTML = `
    <div class="wcs-confirmation__icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
           stroke-linecap="round" stroke-linejoin="round">
        <path d="M20 6 9 17l-5-5" />
      </svg>
    </div>
    <h2 class="wcs-confirmation__title">${title}</h2>
    <p class="wcs-confirmation__subtitle">${subtitle}</p>
    ${rows.length ? `<dl class="wcs-confirmation__details">${rows.join('')}</dl>` : ''}
    <p class="wcs-confirmation__note">
      A confirmation email is on its way${email ? ` to ${escapeHtml(email)}` : ''}.
      You can manage your membership anytime from your account.
    </p>
  `;
  document.getElementById('wcs-root').appendChild(section);
}

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function activeForm() {
  const sel = store.get().selection;
  return store.get().init?.signupForms?.find((f) => f.id === sel.signupFormId) || null;
}

function activeOption() {
  const f = activeForm();
  return f?.options?.[store.get().activeOptionIndex] || null;
}

/**
 * Build the request body for /preview and /confirm.
 *
 * Notes:
 *   - The `customer` block is only sent when we don't have a customer token.
 *     The server uses the token-resolved identity when one is present.
 *     (We always send `birthdate` if we have it — required when birthDateRequired.)
 *   - `paymentMethodGId` / `newCard` are only sent when the form requires
 *     a payment method.
 *   - `customAttributes` is omitted when empty so we don't bloat the body.
 */
function buildRequestBody(sel) {
  const form = activeForm();
  const out = {
    signupToken:    sel.signupToken,
    signupFormId:   sel.signupFormId,
    sellingPlanGId: sel.sellingPlanGId,
    lines:          sel.lines,
    deliveryMethod: { type: sel.deliveryMethod.type },
  };

  // Customer block — only when no token.
  const hasToken = !!(typeof window !== 'undefined' &&
    (window.WineClubCustomerToken ||
     window.localStorage?.getItem?.('wc_customer_token')));
  if (!hasToken) {
    out.customer = { ...sel.customer };
  } else if (sel.customer.birthdate) {
    out.customer = { birthdate: sel.customer.birthdate };
  }
  // birthdate is optional; the API rejects an empty string
  // ("customer.birthdate is not allowed to be empty"), so never send it blank.
  // Guard both branches — regressed once when this block was refactored.
  if (out.customer && !out.customer.birthdate) delete out.customer.birthdate;

  if (sel.deliveryMethod.type === 'SHIPPING') {
    out.deliveryMethod.address = sel.deliveryMethod.address;
  } else {
    out.deliveryMethod.pickupLocationId = sel.deliveryMethod.pickupLocationId;
  }

  if (form?.requiresPaymentMethod !== false) {
    if (sel.paymentMethodGId) {
      out.paymentMethodGId = sel.paymentMethodGId;
    } else if (sel.newCard?.sessionId) {
      out.newCard = { sessionId: sel.newCard.sessionId };
    }
  }

  if (sel.customAttributes && sel.customAttributes.length) {
    out.customAttributes = sel.customAttributes;
  }

  return out;
}
